/**
 * useGetDropAchievements — achievement progress for the current user on a Drop.
 *
 * Returns every active achievement with unlock status and per-condition
 * progress. Authenticated visitors read GET /drops/:slug/me/achievements;
 * anonymous ones read the public GET /drops/:slug/achievements, whose entries
 * carry no progress — so they are normalised into the same shape with zeroed
 * progress, and the widget renders one way regardless of auth state.
 *
 * Types come from @devour/client. They previously could not: both endpoints
 * declared `properties: {}` in swagger, so the generated types were empty and
 * this file hand-wrote the response shape behind an `as unknown as` cast. That
 * hid a real mismatch — the local type claimed `dropId` on an achievement while
 * the API has always returned `surfaceId`. With the schemas filled in, drift like
 * that now fails the build instead of reading as undefined.
 */

import {useQuery} from "@tanstack/react-query";
import {
    Token,
    DropAchievementsApi,
    MyDropAchievementEntry,
    MyDropAchievementListResponse,
} from "@devour/client";
import getConfig from "@/utils/getConfig";

export type {MyDropAchievementEntry, MyDropAchievementListResponse};

async function fetchMyAchievements(
    slug: string,
    fullToken: Token,
): Promise<MyDropAchievementListResponse> {
    return new DropAchievementsApi(getConfig(fullToken)).getDropsSlugMeAchievements({slug});
}

async function fetchPublicAchievements(slug: string): Promise<MyDropAchievementListResponse> {
    const data = await new DropAchievementsApi(getConfig(null)).getDropsSlugAchievements({slug});
    const achievements = data.achievements ?? [];

    return {
        achievements: achievements.map(achievement => ({
            achievement,
            unlocked: false,
            progress: (achievement.conditions ?? []).map(c => ({
                type: c.type,
                threshold: c.threshold,
                current: 0,
                met: false,
            })),
        })),
        /*
         * An anonymous visitor owns nothing — there is no member to have
         * granted a badge to. Empty, not absent: the widget renders one way
         * regardless of auth state, which is the whole point of this shim.
         */
        inventory: [],
        totalUnlocked: 0,
        totalActive: achievements.length,
        totalOwned: 0,
    };
}

export function useGetDropAchievements(slug?: string, fullToken?: Token) {
    return useQuery({
        queryKey: ["drop-achievements", slug, fullToken?.token ? "auth" : "public"],
        queryFn: () => fullToken?.token
            ? fetchMyAchievements(slug!, fullToken)
            : fetchPublicAchievements(slug!),
        enabled: !!slug,
        staleTime: 1000 * 60 * 5,
    });
}
